<template>
  <Transition name="fade">
    <div class="modal-area" v-if="is_open" style="z-index: 99999;">
      <div class="modal-wrapper" style="max-width: 680px; width: 95vw;">
        <div class="modal-container" style="display: flex; flex-direction: column; max-height: 90vh;">

          <!-- Header -->
          <div class="hstack width-100 bb-1-gray p-3" style="flex-shrink: 0;">
            <h3 class="text-left fs-r-20 fw-600 fc-black mb-0">사진 첨부</h3>
            <span class="ms-2" style="color: #aaa; font-size: 13px; align-self: flex-end; padding-bottom: 3px;">
              {{ images.length }}/{{ maxCount }}
            </span>
            <button type="button" class="btn-close ms-auto" @click="close"></button>
          </div>

          <!-- Body -->
          <div class="vstack p-3 gap-3" style="overflow-y: auto; flex: 1 1 auto;">

            <!-- Drop zone (empty state) -->
            <div
              v-if="images.length === 0"
              class="ip-drop-zone"
              :class="{ 'ip-drop-zone--active': isDragging }"
              @drop.prevent="onDrop"
              @dragover.prevent="isDragging = true"
              @dragleave="isDragging = false"
              @click="$refs.mainInput.click()"
            >
              <input ref="mainInput" type="file" accept="image/*" multiple style="display:none" @change="onFileChange">
              <i class="bi bi-image" style="font-size: 52px; color: #ccc;"></i>
              <p class="mt-2 mb-1" style="color: #999;">클릭하거나 사진을 드래그해서 추가하세요</p>
              <p class="mb-0" style="color: #bbb; font-size: 13px;">{{ maxCount }}장 첨부 가능</p>
            </div>

            <!-- Image area (has images) -->
            <template v-else>

              <!-- 원본 미리보기 -->
              <div v-if="!isCropping" class="ip-preview-wrap">
                <img :src="images[currentIndex].preview" class="ip-preview-img">
              </div>

              <!-- 크롭 오버레이 -->
              <div v-else class="ip-cropper-wrap">
                <Cropper
                  :key="cropperKey"
                  ref="cropper"
                  :src="images[currentIndex] ? images[currentIndex].preview : ''"
                  :stencil-props="aspectRatio ? { aspectRatio } : {}"
                  :default-size="cropDefaultSize"
                  class="ip-cropper"
                />
              </div>

              <!-- Controls -->
              <div class="hstack gap-2 flex-wrap">
                <!-- 편집 모드 전용 버튼 -->
                <template v-if="isCropping">
                  <button type="button" class="btn btn-outline-secondary btn-sm" @click="rotateLeft">
                    <i class="bi bi-arrow-counterclockwise"></i>
                  </button>
                  <button type="button" class="btn btn-outline-secondary btn-sm" @click="rotateRight">
                    <i class="bi bi-arrow-clockwise"></i>
                  </button>
                  <div class="ip-ratio-group">
                    <button type="button" class="ip-ratio-btn" :class="{ active: selectedRatio === 'free' }" @click="setAspectRatio(null, 'free')">자유롭게</button>
                    <button type="button" class="ip-ratio-btn" :class="{ active: selectedRatio === '1:1' }" @click="setAspectRatio(1, '1:1')">1:1</button>
                    <button type="button" class="ip-ratio-btn" :class="{ active: selectedRatio === '4:3' }" @click="setAspectRatio(4/3, '4:3')">4:3</button>
                  </div>
                  <div class="ms-auto hstack gap-2">
                    <button type="button" class="btn btn-outline-secondary btn-sm" @click="cancelCrop">취소</button>
                    <button type="button" class="btn btn-orange btn-sm" @click="applyCrop">저장</button>
                  </div>
                </template>
                <!-- 미리보기 모드: 편집 버튼 -->
                <button v-else type="button" class="btn btn-outline-orange btn-sm mx-auto" @click="startCrop">
                  <i class="bi bi-crop"></i> 편집
                </button>
              </div>

              <!-- Thumbnail strip -->
              <div v-if="!isCropping" class="ip-thumb-strip">
                <div
                  v-for="(img, i) in images"
                  :key="'t' + i"
                  class="ip-thumb"
                  :class="{ 'ip-thumb--active': currentIndex === i }"
                  @click="selectImage(i)"
                >
                  <img :src="img.preview" style="width:100%; height:100%; object-fit:cover;">
                  <button type="button" class="ip-thumb-remove" @click.stop="removeImage(i)">
                    <i class="bi bi-x"></i>
                  </button>
                </div>

                <!-- Add more button -->
                <div
                  v-if="images.length < maxCount"
                  class="ip-thumb ip-thumb-add"
                  @click="$refs.addInput.click()"
                  @drop.prevent="onDrop"
                  @dragover.prevent
                >
                  <input ref="addInput" type="file" accept="image/*" multiple style="display:none" @change="onFileChange">
                  <i class="bi bi-plus-lg" style="font-size: 22px; color: #bbb;"></i>
                </div>
              </div>

            </template>
          </div>

          <!-- Footer -->
          <div v-if="!isCropping" class="hstack p-3 gap-2 justify-content-end" style="border-top: 1px solid #ededed; flex-shrink: 0;">
            <button type="button" class="btn btn-outline-secondary" @click="close">취소</button>
            <button type="button" class="btn btn-orange" :disabled="images.length === 0" @click="complete">완료</button>
          </div>

        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
import { Cropper } from 'vue-advanced-cropper';

export default {
  name: 'ImagePickerModal',
  components: { Cropper },
  data() {
    return {
      is_open: false,
      from: '',
      maxCount: 3,
      aspectRatio: null,
      selectedRatio: 'free',
      isDragging: false,
      images: [],       // [{ preview: dataURL, file: File }]
      currentIndex: 0,
      cropperKey: 0,
      isCropping: false,
    };
  },
  mounted() {
    this.emitter.on('openImagePickerModal', (params) => {
      this.openModal(params);
    });
  },
  methods: {
    openModal(params = {}) {
      this.from = params.from || '';
      this.maxCount = params.maxCount ?? 3;
      this.aspectRatio = params.aspectRatio || null;
      this.isDragging = false;
      this.currentIndex = 0;
      this.cropperKey = 0;
      this.selectedRatio = 'free';
      this.isCropping = false;

      if (params.initialImages && params.initialImages.length > 0) {
        this.images = params.initialImages.map(img => ({ preview: img.preview, file: img.file }));
      } else {
        this.images = [];
      }

      this.is_open = true;
    },
    close() {
      this.is_open = false;
      this.isCropping = false;
      this.images = [];
    },
    onDrop(event) {
      this.isDragging = false;
      const files = Array.from(event.dataTransfer.files).filter(f => f.type.startsWith('image/'));
      this.addFiles(files);
    },
    onFileChange(event) {
      const files = Array.from(event.target.files);
      this.addFiles(files);
      event.target.value = '';
    },
    addFiles(files) {
      const remaining = this.maxCount - this.images.length;
      if (remaining <= 0) return;
      const toAdd = files.slice(0, remaining);

      if (files.length > remaining) {
        this.$root.showToastMsg('', '알림', `${this.maxCount}장 첨부 가능합니다.`, 'default');
      }

      const wasEmpty = this.images.length === 0;

      toAdd.forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const preview = e.target.result;
          this.$common.resizeImage(file).then(resizedFile => {
            this.images.push({ preview, file: resizedFile });
            if (wasEmpty && this.images.length === 1) {
              this.currentIndex = 0;
            }
          });
        };
        reader.readAsDataURL(file);
      });
    },
    cropGuard() {
      if (!this.isCropping) return true;
      return confirm('변경사항이 저장되지 않았습니다. 이동하시겠습니까?');
    },
    // 썸네일 클릭: 크롭 모드 취소 후 이미지 전환
    selectImage(index) {
      if (this.currentIndex === index) {
        this.isCropping = false;
        return;
      }
      if (!this.cropGuard()) return;
      this.isCropping = false;
      this.selectedRatio = 'free';
      this.aspectRatio = null;
      this.currentIndex = index;
    },
    removeImage(index) {
      if (!this.cropGuard()) return;
      this.isCropping = false;
      this.images.splice(index, 1);
      if (this.images.length === 0) {
        this.currentIndex = 0;
        return;
      }
      if (this.currentIndex >= this.images.length) {
        this.currentIndex = this.images.length - 1;
      }
    },
    cancelCrop() {
      this.isCropping = false;
      this.selectedRatio = 'free';
      this.aspectRatio = null;
    },
    // "편집" 버튼: 크로퍼 오버레이 활성
    startCrop() {
      this.selectedRatio = 'free';
      this.aspectRatio = null;
      this.cropperKey++;
      this.isCropping = true;
    },
    rotateLeft() {
      if (this.isCropping) {
        this.$refs.cropper?.rotate(-90);
      } else {
        this.rotateDirect(-90);
      }
    },
    rotateRight() {
      if (this.isCropping) {
        this.$refs.cropper?.rotate(90);
      } else {
        this.rotateDirect(90);
      }
    },
    rotateDirect(degrees) {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.height;
        canvas.height = img.width;
        const ctx = canvas.getContext('2d');
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((degrees * Math.PI) / 180);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
        canvas.toBlob(blob => {
          const origName = this.images[this.currentIndex].file?.name || 'image.jpg';
          this.images[this.currentIndex].preview = dataUrl;
          this.images[this.currentIndex].file = new File([blob], origName, { type: 'image/jpeg' });
        }, 'image/jpeg', 0.9);
      };
      img.src = this.images[this.currentIndex].preview;
    },
    setAspectRatio(ratio, label) {
      this.aspectRatio = ratio;
      this.selectedRatio = label;
      // 크롭 모드가 아니면 자동 진입
      if (!this.isCropping) {
        this.cropperKey++;
        this.isCropping = true;
      }
      this.$nextTick(() => {
        this.$refs.cropper?.setCoordinates(({ imageSize }) => ({
          top: 0,
          left: ratio ? Math.max(0, (imageSize.width - imageSize.height * ratio) / 2) : 0,
          width: ratio ? Math.min(imageSize.width, imageSize.height * ratio) : imageSize.width,
          height: imageSize.height,
        }));
      });
    },
    cropDefaultSize({ imageSize }) {
      return { width: imageSize.width, height: imageSize.height };
    },
    // "✓ 크롭적용" 버튼: 현재 사진에만 크롭 적용 후 미리보기 복귀
    applyCrop() {
      const { canvas } = this.$refs.cropper.getResult();
      if (!canvas) return;
      const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
      canvas.toBlob(blob => {
        const origName = this.images[this.currentIndex].file?.name || 'image.jpg';
        const file = new File([blob], origName, { type: 'image/jpeg' });
        this.images[this.currentIndex].preview = dataUrl;
        this.images[this.currentIndex].file = file;
        this.isCropping = false;
        this.selectedRatio = 'free';
        this.aspectRatio = null;
      }, 'image/jpeg', 0.9);
    },
    // "완료" 버튼: 모든 이미지 결과 전달
    complete() {
      if (this.images.length === 0) return;
      if (!this.cropGuard()) return;
      this.emitter.emit('imagePickerResult', {
        from: this.from,
        files: this.images.map(img => ({ file: img.file, preview: img.preview })),
      });
      this.close();
    },
  },
};
</script>

<style scoped>
@import '@/assets/css/common/common.css';
@import '@/assets/css/style/style.css';

.ip-drop-zone {
  border: 2px dashed #ddd;
  border-radius: 12px;
  padding: 60px 20px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  transition: border-color 0.2s, background 0.2s;
  user-select: none;
}
.ip-drop-zone:hover,
.ip-drop-zone--active {
  border-color: #f6951c;
  background: #fff8f0;
}

/* 원본 미리보기 */
.ip-preview-wrap {
  width: 100%;
  height: 360px;
  background: #1a1a1a;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ip-preview-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

/* 크로퍼 */
.ip-cropper-wrap {
  width: 100%;
  height: 360px;
  background: #1a1a1a;
  border-radius: 8px;
  overflow: visible;
}
.ip-cropper {
  width: 100%;
  height: 100%;
}

.ip-thumb-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.ip-thumb {
  position: relative;
  width: 72px;
  height: 72px;
  border: 2px solid #ededed;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  flex-shrink: 0;
  transition: border-color 0.15s;
}
.ip-thumb:hover,
.ip-thumb--active {
  border-color: #f6951c;
}
.ip-thumb-remove {
  position: absolute;
  top: 2px;
  right: 2px;
  background: rgba(0, 0, 0, 0.55);
  border: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  color: #fff;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  line-height: 1;
}
.ip-thumb-add {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f9f9f9;
  border-style: dashed;
}
.ip-thumb-add:hover {
  background: #fff5e6;
  border-color: #f6951c;
}

/* ── Cropper stencil 커스텀 ── */

:deep(.vue-rectangle-stencil) {
  border: 1.5px solid rgba(255, 255, 255, 0.6);
  box-sizing: border-box;
}

:deep(.vue-rectangle-stencil)::after {
  content: '';
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.8) 2px, transparent 2px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.8) 2px, transparent 2px);
  background-size: 33.333% 33.333%;
  pointer-events: none;
}

:deep(.vue-simple-line) {
  background: transparent !important;
}

:deep(.vue-handler-wrapper--west-north),
:deep(.vue-handler-wrapper--east-north),
:deep(.vue-handler-wrapper--west-south),
:deep(.vue-handler-wrapper--east-south) {
  width: 22px !important;
  height: 22px !important;
}

/* 핸들을 박스 바깥쪽 모서리에 위치
   absolute 배치는 부모 padding edge(border 안쪽) 기준이므로
   stencil border-width(1.5px)만큼 바깥 방향으로 추가 보정 */
:deep(.vue-handler-wrapper--west-north) { transform: translate(calc(-18%), calc(-18%)) !important; }
:deep(.vue-handler-wrapper--east-north) { transform: translate(calc(-82%), calc(-18%)) !important; }
:deep(.vue-handler-wrapper--west-south) { transform: translate(calc(-18%), calc(-82%)) !important; }
:deep(.vue-handler-wrapper--east-south) { transform: translate(calc(-82%), calc(-82%)) !important; }

:deep(.vue-simple-handler) {
  background: transparent !important;
  border: 3.5px solid #fff !important;
  border-radius: 0 !important;
  width: 100% !important;
  height: 100% !important;
}
:deep(.vue-handler-wrapper--west-north .vue-simple-handler) {
  border-right: none !important;
  border-bottom: none !important;
}
:deep(.vue-handler-wrapper--east-north .vue-simple-handler) {
  border-left: none !important;
  border-bottom: none !important;
}
:deep(.vue-handler-wrapper--west-south .vue-simple-handler) {
  border-right: none !important;
  border-top: none !important;
}
:deep(.vue-handler-wrapper--east-south .vue-simple-handler) {
  border-left: none !important;
  border-top: none !important;
}
:deep(.vue-handler-wrapper--north .vue-simple-handler),
:deep(.vue-handler-wrapper--south .vue-simple-handler),
:deep(.vue-handler-wrapper--west .vue-simple-handler),
:deep(.vue-handler-wrapper--east .vue-simple-handler) {
  display: none !important;
}

.ip-ratio-group {
  display: flex;
  border: 1px solid #6c757d;
  border-radius: 0.2rem;
  overflow: hidden;
}
.ip-ratio-btn {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  border: none;
  background: #fff;
  color: #6c757d;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  line-height: 1.5;
}
.ip-ratio-btn + .ip-ratio-btn {
  border-left: 1px solid #6c757d;
}
.ip-ratio-btn:hover {
  background: #f5f5f5;
}
.ip-ratio-btn.active {
  background: #6c757d;
  color: #fff;
  border-color: #6c757d;
}
.ip-ratio-btn.active + .ip-ratio-btn {
  border-left-color: #6c757d;
}
</style>
